import {
  flexRender,
  type Row,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import type * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import {
  Sortable,
  SortableContent,
  SortableItem,
} from '@/components/ui/sortable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> extends React.ComponentProps<'div'> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  bodyFooter?: React.ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  showPagination?: boolean;
}

export function DataTable<TData>({
  table,
  actionBar,
  bodyFooter,
  onRowClick,
  showPagination = true,
  children,
  className,
  ...props
}: DataTableProps<TData>) {
  const { t } = useTranslation('table');
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const headerGroups = table.getHeaderGroups();

  useEffect(() => {
    if (showPagination) {
      tableContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const columnOrder = useMemo(() => {
    if (headerGroups.length > 0) {
      return headerGroups[0].headers
        .map((header) => header.column.id)
        .filter(Boolean) as string[];
    }
    return [];
  }, [headerGroups]);

  const onColumnOrderChange = (newOrder: string[]) => {
    const allColumnIds = table
      .getAllColumns()
      .map((col) => col.id)
      .filter(Boolean) as string[];

    const hiddenColumnIds = allColumnIds.filter((id) => !newOrder.includes(id));

    const fullOrder = [...newOrder, ...hiddenColumnIds];
    table.setColumnOrder(fullOrder);
  };

  return (
    <div className={cn('flex w-full flex-col gap-2.5', className)} {...props}>
      {children}
      <Sortable
        onValueChange={onColumnOrderChange}
        orientation='horizontal'
        value={columnOrder}
      >
        <div
          className='max-h-full min-h-0 overflow-auto rounded-md border'
          ref={tableContainerRef}
        >
          <Table>
            <TableHeader className='sticky top-0 z-10'>
              {headerGroups.map((headerGroup) => (
                <TableRow className='hover:bg-transparent' key={headerGroup.id}>
                  <SortableContent asChild className='contents' withoutSlot>
                    {headerGroup.headers.map((header) => {
                      const canReorder =
                        header.column.columnDef.meta?.enableColumnReordering !==
                        false;
                      return (
                        <SortableItem
                          asChild
                          disabled={!canReorder}
                          key={header.id}
                          value={header.column.id}
                        >
                          <TableHead
                            className='bg-background'
                            colSpan={header.colSpan}
                            style={{
                              ...getCommonPinningStyles({
                                column: header.column,
                              }),
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        </SortableItem>
                      );
                    })}
                  </SortableContent>
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      table.options.enableRowSelection
                        ? 'bg-muted data-[state=selected]:bg-background'
                        : 'bg-background',
                    )}
                    data-state={row.getIsSelected() && 'selected'}
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          ...getCommonPinningStyles({ column: cell.column }),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    className='h-24 text-center'
                    colSpan={table.getAllColumns().length}
                  >
                    {t('no results')}
                  </TableCell>
                </TableRow>
              )}
              {bodyFooter ? (
                <TableRow>
                  <TableCell colSpan={table.getAllColumns().length}>
                    {bodyFooter}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </Sortable>
      {showPagination ? (
        <div className='flex flex-col gap-2.5'>
          <DataTablePagination table={table} />
          {actionBar &&
            table.getFilteredSelectedRowModel().rows.length > 0 &&
            actionBar}
        </div>
      ) : null}
    </div>
  );
}
